import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle, AlertCircle, Download, Eye, ArrowLeft, RefreshCw } from "lucide-react";
import { toast } from "sonner";

interface DigitalContent {
  id: string;
  title: string;
  description: string;
  file_url: string;
  cover_image_url: string;
  price_cents: number;
  mentor_id: string;
  mentor_info?: {
    id: string;
    profile_id: string;
    profiles?: {
      id: string;
      nickname: string;
    };
  };
}

interface PurchaseRecord {
  id: string;
  buyer_id: string;
  digital_content_id: string;
  created_at: string;
  payment_status: string;
  access_granted_at: string | null;
}

interface ValidationResult {
  contentExists: boolean;
  purchaseExists: boolean;
  accessGranted: boolean;
  mentorDataValid: boolean;
  userDataValid: boolean;
  allChecksPass: boolean;
  errors: string[];
}

export default function DigitalContentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState<DigitalContent | null>(null);
  const [purchase, setPurchase] = useState<PurchaseRecord | null>(null);
  const [validation, setValidation] = useState<ValidationResult | null>(null);
  const [user, setUser] = useState<any>(null);

  const contentId = searchParams.get("content_id");

  useEffect(() => {
    if (!contentId) {
      toast.error("ID do conteúdo não encontrado na URL");
      navigate("/");
      return;
    }

    validatePurchaseAndContent();
  }, [contentId]);

  const validatePurchaseAndContent = async () => {
    console.log("🔍 [DigitalContentSuccess] Iniciando validação completa...");
    setLoading(true);

    try {
      // 1. Verificar usuário autenticado
      const { data: { user: currentUser }, error: userError } = await supabase.auth.getUser();
      
      if (userError || !currentUser) {
        console.error("❌ [DigitalContentSuccess] Usuário não autenticado:", userError);
        toast.error("Usuário não autenticado");
        navigate("/auth");
        return;
      }

      setUser(currentUser);
      console.log("✅ [DigitalContentSuccess] Usuário autenticado:", currentUser.id);

      // 2. Buscar dados do conteúdo digital
      console.log("🔍 [DigitalContentSuccess] Buscando conteúdo digital:", contentId);
      
      const { data: contentData, error: contentError } = await supabase
        .from("digital_contents")
        .select(`
          id,
          title,
          description,
          file_url,
          cover_image_url,
          price_cents,
          mentor_id,
          mentor_info:mentor_id (
            id,
            profile_id,
            profiles:profile_id (
              id,
              nickname
            )
          )
        `)
        .eq("id", contentId)
        .single();

      if (contentError) {
        console.error("❌ [DigitalContentSuccess] Erro ao buscar conteúdo:", contentError);
      } else {
        console.log("✅ [DigitalContentSuccess] Conteúdo encontrado:", contentData);
        setContent(contentData);
      }

      // 3. Verificar se existe registro de compra
      console.log("🔍 [DigitalContentSuccess] Verificando registro de compra...");
      
      const { data: purchaseData, error: purchaseError } = await supabase
        .from("digital_content_purchases")
        .select("*")
        .eq("buyer_id", currentUser.id)
        .eq("digital_content_id", contentId)
        .order("created_at", { ascending: false })
        .limit(1);

      if (purchaseError) {
        console.error("❌ [DigitalContentSuccess] Erro ao buscar compra:", purchaseError);
      } else {
        console.log("✅ [DigitalContentSuccess] Dados de compra:", purchaseData);
        if (purchaseData && purchaseData.length > 0) {
          setPurchase(purchaseData[0]);
        }
      }

      // 4. Executar validações completas
      const validationResult = performValidation(
        contentData,
        purchaseData?.[0],
        currentUser
      );

      setValidation(validationResult);
      console.log("📊 [DigitalContentSuccess] Resultado da validação:", validationResult);

      // 5. Se a compra existe mas o acesso não foi liberado, liberar automaticamente
      if (contentData && purchaseData?.[0] && !validationResult.accessGranted) {
        console.log("🔄 [DigitalContentSuccess] Liberando acesso automaticamente...");
        await ensureAccess(currentUser.id, contentId);
        
        // Atualizar estado local imediatamente para evitar loop
        const updatedPurchase = { ...purchaseData[0], access_granted_at: new Date().toISOString(), payment_status: 'completed' };
        setPurchase(updatedPurchase);
        
        // Recalcular validação com dados atualizados
        const updatedValidation = performValidation(contentData, updatedPurchase, currentUser);
        setValidation(updatedValidation);
      }

      // 6. NOVO: Se não existe compra mas o usuário chegou aqui via Stripe success, criar registro
      if (contentData && !purchaseData?.[0]) {
        console.log("🚨 [DigitalContentSuccess] WEBHOOK FALHOU - Criando registro de emergência...");
        await ensureAccess(currentUser.id, contentId);
        
        // Revalidar após criar o registro
        const { data: newPurchaseData } = await supabase
          .from("digital_content_purchases")
          .select("*")
          .eq("buyer_id", currentUser.id)
          .eq("digital_content_id", contentId)
          .order("created_at", { ascending: false })
          .limit(1);

        if (newPurchaseData && newPurchaseData.length > 0) {
          setPurchase(newPurchaseData[0]);
          const updatedValidation = performValidation(contentData, newPurchaseData[0], currentUser);
          setValidation(updatedValidation);
          
          // 🎯 ENVIAR EMAILS DE FALLBACK (webhook pode ter falhado)
          await sendFallbackEmails(contentData, newPurchaseData[0], currentUser);
          
          toast.success("🎉 Compra processada com sucesso! O webhook falhou, mas corrigimos automaticamente.");
        }
      }

    } catch (error) {
      console.error("💥 [DigitalContentSuccess] Erro geral:", error);
      toast.error("Erro ao validar compra");
    } finally {
      setLoading(false);
    }
  };

  const performValidation = (
    contentData: any,
    purchaseData: any,
    userData: any
  ): ValidationResult => {
    const errors: string[] = [];
    
    // Verificar se conteúdo existe
    const contentExists = !!contentData;
    if (!contentExists) {
      errors.push("Conteúdo digital não encontrado no banco de dados");
    }

    // Verificar se compra existe
    const purchaseExists = !!purchaseData;
    if (!purchaseExists) {
      errors.push("Registro de compra não encontrado");
    }

    // Verificar se acesso foi concedido (usando access_granted_at ao invés de access_granted)
    const accessGranted = purchaseData?.payment_status === 'completed' || !!purchaseData?.access_granted_at;
    if (purchaseExists && !accessGranted) {
      errors.push("Acesso ainda não foi concedido automaticamente");
    }

    // Verificar dados do mentor
    const mentorDataValid = !!(
      contentData?.mentor_id &&
      contentData?.mentor_info?.id &&
      contentData?.mentor_info?.profiles?.nickname
    );
    if (contentExists && !mentorDataValid) {
      errors.push("Dados do mentor incompletos ou referências incorretas");
    }

    // Verificar dados do usuário
    const userDataValid = !!(userData?.id && userData?.email);
    if (!userDataValid) {
      errors.push("Dados do usuário inválidos");
    }

    const allChecksPass = contentExists && purchaseExists && accessGranted && mentorDataValid && userDataValid;

    return {
      contentExists,
      purchaseExists,
      accessGranted,
      mentorDataValid,
      userDataValid,
      allChecksPass,
      errors
    };
  };

  const ensureAccess = async (userId: string, contentId: string) => {
    console.log("🔐 [DigitalContentSuccess] Garantindo acesso ao usuário...");
    
    try {
      // Primeiro, verificar se já existe um registro de compra
      const { data: existingPurchase, error: checkError } = await supabase
        .from("digital_content_purchases")
        .select("*")
        .eq("buyer_id", userId)
        .eq("digital_content_id", contentId)
        .maybeSingle();

      if (checkError) {
        console.error("❌ [DigitalContentSuccess] Erro ao verificar compra existente:", checkError);
      }

      if (existingPurchase) {
        console.log("✅ [DigitalContentSuccess] Compra existente encontrada:", existingPurchase);
        
        // Se existe mas não tem acesso, liberar
        if (!existingPurchase.access_granted_at || existingPurchase.payment_status !== 'completed') {
          const { data: updateData, error: updateError } = await supabase
            .from("digital_content_purchases")
            .update({ 
              access_granted_at: new Date().toISOString(),
              payment_status: 'completed'
            })
            .eq("id", existingPurchase.id)
            .select();

          if (updateError) {
            console.error("❌ [DigitalContentSuccess] Erro ao atualizar acesso:", updateError);
            toast.error("Erro ao liberar acesso. Tente novamente.");
          } else {
            console.log("✅ [DigitalContentSuccess] Acesso atualizado com sucesso:", updateData);
            toast.success("🎉 Acesso ao conteúdo liberado com sucesso!");
          }
        } else {
          console.log("✅ [DigitalContentSuccess] Acesso já estava liberado");
          toast.success("🎉 Acesso ao conteúdo confirmado!");
        }
      } else {
        console.log("⚠️ [DigitalContentSuccess] Nenhuma compra encontrada - criando registro de fallback");
        
        // Se chegou aqui via Stripe success, significa que o pagamento foi aprovado
        // Vamos criar o registro que o webhook deveria ter criado
        const { data: contentData, error: contentError } = await supabase
          .from("digital_contents")
          .select("price_cents")
          .eq("id", contentId)
          .single();

        if (contentError) {
          console.error("❌ [DigitalContentSuccess] Erro ao buscar dados do conteúdo:", contentError);
          toast.error("Erro ao verificar dados do conteúdo.");
          return;
        }

        // Criar registro de compra como fallback (webhook falhou)
        console.log("🚨 [DigitalContentSuccess] CRIANDO REGISTRO DE EMERGÊNCIA");
        console.log("📋 [DigitalContentSuccess] Dados para inserção:", {
          digital_content_id: contentId,
          buyer_id: userId,
          purchase_price_cents: contentData.price_cents || 0,
          payment_status: 'completed',
          payment_method: 'stripe_fallback'
        });
        
        const { data: createData, error: createError } = await supabase
          .from("digital_content_purchases")
          .insert({
            digital_content_id: contentId,
            buyer_id: userId,
            purchase_price_cents: contentData.price_cents || 0,
            payment_status: 'completed',
            payment_method: 'stripe_fallback', // Indicar que foi criado via fallback
            stripe_payment_intent_id: `fallback_${Date.now()}`, // ID único para identificar
            access_granted_at: new Date().toISOString(),
            created_at: new Date().toISOString()
          })
          .select();

        if (createError) {
          console.error("❌ [DigitalContentSuccess] Erro ao criar registro de fallback:", createError);
          toast.error("Erro ao criar registro de acesso. Entre em contato com o suporte.");
        } else {
          console.log("✅ [DigitalContentSuccess] Registro de fallback criado com sucesso:", createData);
          toast.success("🎉 Acesso ao conteúdo liberado com sucesso!");
          
          // Atualizar o estado local
          if (createData && createData[0]) {
            setPurchase(createData[0] as any);
          }
        }
      }
    } catch (error) {
      console.error("💥 [DigitalContentSuccess] Erro ao garantir acesso:", error);
      toast.error("Erro inesperado ao liberar acesso.");
    }
  };

  const handleViewContent = () => {
    if (content?.file_url) {
      window.open(content.file_url, "_blank");
    }
  };

  const handleDownloadContent = () => {
    if (content?.file_url) {
      const link = document.createElement("a");
      link.href = content.file_url;
      link.download = content.title;
      link.click();
    }
  };

  // 🎯 FUNÇÃO PARA ENVIAR EMAILS DE FALLBACK
  const sendFallbackEmails = async (content: any, purchase: any, user: any) => {
    try {
      console.log("📧 [DigitalContentSuccess] Iniciando envio de emails de fallback...");
      
      // Buscar dados do mentor
      const { data: mentorData, error: mentorError } = await supabase
        .from("digital_contents")
        .select(`
          mentor:mentor_id (
            profiles:profile_id (
              email,
              nickname
            )
          )
        `)
        .eq("id", content.id)
        .single();

      if (mentorError) {
        console.error("❌ [DigitalContentSuccess] Erro ao buscar dados do mentor:", mentorError);
        return;
      }

      const mentorEmail = mentorData?.mentor?.profiles?.email;
      const mentorName = mentorData?.mentor?.profiles?.nickname || "Mentor";
      const buyerEmail = user.email;
      const buyerName = user.user_metadata?.nickname || user.user_metadata?.full_name || "Jogador";
      const contentTitle = content.title;
      const contentType = content.content_type || "Conteúdo Digital";
      const priceCents = purchase.purchase_price_cents || 0;
      const purchaseDate = new Date(purchase.created_at).toLocaleDateString('pt-BR');

      console.log("📋 [DigitalContentSuccess] Dados para emails:", {
        buyerEmail,
        mentorEmail,
        contentTitle,
        priceCents
      });

      // 1. EMAIL PARA O COMPRADOR
      if (buyerEmail) {
        console.log("📧 [DigitalContentSuccess] Enviando email de confirmação para comprador...");
        
        try {
          const { error: buyerEmailError } = await supabase.functions.invoke('send-email', {
            body: {
              to: buyerEmail,
              template: 'digitalContentPurchaseConfirmation',
              data: {
                contentTitle,
                mentorName,
                contentType,
                priceCents,
                purchaseDate,
                appUrl: window.location.origin
              }
            }
          });

          if (buyerEmailError) {
            console.error("❌ [DigitalContentSuccess] Erro ao enviar email para comprador:", buyerEmailError);
          } else {
            console.log("✅ [DigitalContentSuccess] Email de confirmação enviado para comprador");
          }
        } catch (error) {
          console.error("❌ [DigitalContentSuccess] Erro no envio do email para comprador:", error);
        }
      }

      // 2. EMAIL PARA O MENTOR
      if (mentorEmail) {
        console.log("📧 [DigitalContentSuccess] Enviando email de nova venda para mentor...");
        
        try {
          const { error: mentorEmailError } = await supabase.functions.invoke('send-email', {
            body: {
              to: mentorEmail,
              template: 'digitalContentSold',
              data: {
                contentTitle,
                buyerName,
                contentType,
                priceCents,
                saleDate: purchaseDate,
                appUrl: window.location.origin
              }
            }
          });

          if (mentorEmailError) {
            console.error("❌ [DigitalContentSuccess] Erro ao enviar email para mentor:", mentorEmailError);
          } else {
            console.log("✅ [DigitalContentSuccess] Email de nova venda enviado para mentor");
          }
        } catch (error) {
          console.error("❌ [DigitalContentSuccess] Erro no envio do email para mentor:", error);
        }
      }

    } catch (error) {
      console.error("❌ [DigitalContentSuccess] Erro geral no sistema de emails de fallback:", error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p>Validando sua compra...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0F141C] py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            {validation?.allChecksPass ? (
              <CheckCircle className="h-16 w-16 text-green-500" />
            ) : (
              <AlertCircle className="h-16 w-16 text-orange-500" />
            )}
          </div>
          <h1 className="text-3xl font-bold mb-2 text-white">
            {validation?.allChecksPass ? "Compra Realizada com Sucesso!" : "Validando Compra..."}
          </h1>
          <p className="text-gray-400">
            {validation?.allChecksPass 
              ? "Seu conteúdo digital está pronto para acesso"
              : "Verificando dados da compra e configurando acesso"
            }
          </p>
        </div>

        {/* Conteúdo Comprado */}
        {content && (
          <Card className="mb-6 bg-[#1A2233] border-[#222A35] text-white">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-white">
                <Download className="h-5 w-5 text-[#00B6F0]" />
                Conteúdo Adquirido
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-4">
                {content.cover_image_url && (
                  <img 
                    src={content.cover_image_url} 
                    alt={content.title}
                    className="w-24 h-24 object-cover rounded-lg"
                  />
                )}
                <div className="flex-1">
                  <h3 className="font-semibold text-lg text-white">{content.title}</h3>
                  <p className="text-gray-400 mb-2">{content.description}</p>
                  <p className="text-sm text-gray-500">
                    Mentor: {content.mentor_info?.profiles?.nickname || "Não identificado"}
                  </p>
                  <p className="text-sm font-medium text-[#00B6F0]">
                    Valor: R$ {(content.price_cents / 100).toFixed(2)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Status da Validação */}
        <Card className="mb-6 bg-[#1A2233] border-[#222A35] text-white">
          <CardHeader>
            <CardTitle className="text-white">Status da Validação</CardTitle>
            <CardDescription className="text-gray-400">Verificação automática dos dados da compra</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                {validation?.contentExists ? (
                  <CheckCircle className="h-4 w-4 text-green-500" />
                ) : (
                  <AlertCircle className="h-4 w-4 text-red-500" />
                )}
                <span className="text-gray-300">Conteúdo digital encontrado no banco</span>
              </div>
              
              <div className="flex items-center gap-2">
                {validation?.purchaseExists ? (
                  <CheckCircle className="h-4 w-4 text-green-500" />
                ) : (
                  <AlertCircle className="h-4 w-4 text-red-500" />
                )}
                <span className="text-gray-300">Registro de compra confirmado</span>
              </div>
              
              <div className="flex items-center gap-2">
                {validation?.accessGranted ? (
                  <CheckCircle className="h-4 w-4 text-green-500" />
                ) : (
                  <AlertCircle className="h-4 w-4 text-orange-500" />
                )}
                <span className="text-gray-300">Acesso ao conteúdo liberado</span>
              </div>
              
              <div className="flex items-center gap-2">
                {validation?.mentorDataValid ? (
                  <CheckCircle className="h-4 w-4 text-green-500" />
                ) : (
                  <AlertCircle className="h-4 w-4 text-red-500" />
                )}
                <span className="text-gray-300">Dados do mentor válidos</span>
              </div>
              
              <div className="flex items-center gap-2">
                {validation?.userDataValid ? (
                  <CheckCircle className="h-4 w-4 text-green-500" />
                ) : (
                  <AlertCircle className="h-4 w-4 text-red-500" />
                )}
                <span className="text-gray-300">Dados do usuário válidos</span>
              </div>
            </div>

            {validation?.errors && validation.errors.length > 0 && (
              <div className="mt-4 p-3 bg-orange-500/20 border border-orange-500/30 rounded-lg">
                <h4 className="font-medium text-orange-400 mb-2">Problemas Detectados:</h4>
                <ul className="text-sm text-orange-300 space-y-1">
                  {validation.errors.map((error, index) => (
                    <li key={index}>• {error}</li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Dados Técnicos */}
        <Card className="mb-6 bg-[#1A2233] border-[#222A35] text-white">
          <CardHeader>
            <CardTitle className="text-white">Dados Técnicos</CardTitle>
            <CardDescription className="text-gray-400">Informações para debug e suporte</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <strong className="text-gray-300">ID do Conteúdo:</strong> <span className="text-gray-400">{contentId}</span>
              </div>
              <div>
                <strong className="text-gray-300">ID do Usuário:</strong> <span className="text-gray-400">{user?.id}</span>
              </div>
              <div>
                <strong className="text-gray-300">Email:</strong> <span className="text-gray-400">{user?.email}</span>
              </div>
              <div>
                <strong className="text-gray-300">ID do Mentor:</strong> <span className="text-gray-400">{content?.mentor_id}</span>
              </div>
              <div>
                <strong className="text-gray-300">Data da Compra:</strong> <span className="text-gray-400">{purchase?.created_at ? new Date(purchase.created_at).toLocaleString('pt-BR', { 
                  timeZone: 'America/Sao_Paulo',
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit'
                }) : "Não encontrada"}</span>
              </div>
              <div>
                <strong className="text-gray-300">Acesso Liberado:</strong> <span className="text-gray-400">{purchase?.access_granted_at ? "Sim" : "Não"}</span>
              </div>
            </div>
            
            {/* Aviso sobre sistema de correção automática */}
            <div className="mt-4 p-3 bg-blue-500/20 border border-blue-500/30 rounded-lg">
              <h4 className="font-medium text-blue-400 mb-2">🔧 Sistema de Correção Automática</h4>
              <p className="text-sm text-blue-300">
                Esta página possui um sistema de fallback que garante o acesso ao conteúdo mesmo se o webhook do Stripe falhar. 
                Se você chegou aqui após um pagamento aprovado, o acesso será liberado automaticamente.
              </p>
              <p className="text-xs text-blue-400 mt-2">
                <strong>Monitoramento:</strong> O sistema verifica automaticamente compras pendentes a cada 30 segundos na página "Meus Conteúdos".
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Ações */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          {validation?.allChecksPass && content?.file_url && (
            <>
              <Button onClick={handleViewContent} className="flex items-center gap-2 bg-[#00B6F0] hover:bg-[#0099CC] text-black">
                <Eye className="h-4 w-4" />
                Visualizar Conteúdo
              </Button>
              
              <Button onClick={handleDownloadContent} variant="outline" className="flex items-center gap-2 border-[#00B6F0] text-[#00B6F0] hover:bg-[#00B6F0] hover:text-black">
                <Download className="h-4 w-4" />
                Baixar Conteúdo
              </Button>
            </>
          )}
          
          <Button 
            onClick={() => navigate("/painel-jogador/meus-conteudos")} 
            variant="outline"
            className="flex items-center gap-2 border-[#00B6F0] text-[#00B6F0] hover:bg-[#00B6F0] hover:text-black"
          >
            <ArrowLeft className="h-4 w-4" />
            Meus Conteúdos
          </Button>

          <Button 
            onClick={validatePurchaseAndContent} 
            variant="outline"
            className="flex items-center gap-2 border-gray-600 text-gray-400 hover:bg-gray-600 hover:text-white"
          >
            <RefreshCw className="h-4 w-4" />
            Revalidar
          </Button>
        </div>

        {!validation?.allChecksPass && (
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-400">
              Se o problema persistir, entre em contato com o suporte informando o ID da compra.
            </p>
          </div>
        )}
      </div>
    </div>
  );
} 